import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { GetCharitiesQuery as GetCharities } from '__generated__/graphql';

import GetCharitiesQuery from '../../common/Fundraising/GetCharitiesQuery';
import UI from '../../common/UI';
import useFocusIndex from '../../common/useFocusIndex';
import useOnClickOutside from '../../common/useOnClickOutside';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

export type Charity = GetCharities['project']['charities']['data'][0];

interface CharityFilterParams {
  search?: string;
  event_id?: string;
}

export type CharityPickerPreselector = (
  (charities: Charity[])=> Charity
);

export interface CharityPickerProps {
  charity?: Charity;
  filters?: CharityFilterParams;
  onChange?: (charity: Charity) => void;
  preselect?: CharityPickerPreselector;
  renderActive?: (charity?: Charity) => ReactNode;
  nullable?: boolean;
  disabled?: boolean;
  noneOption?: ReactNode;
}

const CharityPicker = ({
  charity, filters, onChange, preselect, renderActive,
  nullable = true, disabled = false, noneOption,
}: CharityPickerProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const [limit, setLimit] = useState(20);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(filters?.search || '');
  const searchRef = useRef<HTMLInputElement>();

  const { data, loading } = useQuery(
    GetCharitiesQuery,
    {
      variables: {
        project_id: project.id,
        ...filters,
        search,
        per_page: limit,
        page: 1,
      },
      skip: !preselect && !isOpen && !charity,
    },
  );

  const charities = useMemo(
    () => data?.project.charities.data || [],
    [data?.project.charities.data],
  );

  const resetSearch = () => {
    setSearch('');

    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  /** Index 0 is reserved for 'all charities' */
  const activeIndex = charities.findIndex(({ id }) => id === charity?.id) + 1;
  const maxIndex = charities.length - 1 + 1;

  /* eslint-disable @typescript-eslint/no-use-before-define */
  const [focusIndex, setFocusIndex, listRef, scrollToIndex] = useFocusIndex({
    initialIndex: Math.max(0, activeIndex),
    maxIndex,
    active: isOpen,
    onSelect: (index) => setCharity(charities[index - 1]?.id),
    onCancel: () => close(),
  });
  /* eslint-enable @typescript-eslint/no-use-before-define */

  const handleSearch = (charity: ChangeEvent<HTMLInputElement>) => {
    setSearch(charity.target.value);
  };

  useEffect(() => {
    const min = search ? Math.min(1, maxIndex) : 0;
    setFocusIndex(Math.max(min, activeIndex));
  }, [search, maxIndex, setFocusIndex, activeIndex, isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = () => {
    setIsOpen(true);
    scrollToIndex(focusIndex);
  };

  const setCharity = useCallback((id?: string) => {
    onChange?.(charities.filter((charity) => charity.id === id)[0]);
    close();
  }, [onChange, charities, close]);

  const toggleDropdown = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    }
  }, [isOpen]);

  const containerRef = useRef<HTMLDivElement>();

  useOnClickOutside(containerRef, () => {
    close();
  });

  useEffect(() => {
    if (data && preselect) {
      setCharity(preselect(charities)?.id);
    }
  }, [data, preselect, setCharity, charities]);

  return (
    <UI.PickerContainer open={isOpen} ref={containerRef}>
      <UI.PickerDropdown open={isOpen}>
        <button type="button" onClick={() => toggleDropdown()} disabled={disabled}>
          <UI.Span>
            <UI.Truncate>
              {charity && (renderActive?.(charity) || (
                <UI.Span>
                  {charity.title}
                </UI.Span>
              ))}
              {!charity && (noneOption || <UI.Span sc={{ color: 'gray.400' }}>{t('choose_charity')}</UI.Span>)}
            </UI.Truncate>
            <UI.Icon>
              <ChevronDown />
            </UI.Icon>
          </UI.Span>
        </button>
        {isOpen && (
          <UI.Div>
            <UI.InputGroup sc={{ mb: 2 }}>
              <UI.InputWrapper>
                <UI.Span>
                  <UI.Icon>
                    <Search />
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder={t('search')}
                  ref={searchRef}
                  sc={{ px: 5 }}
                />
                {search && (
                  <UI.Span>
                    <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <X />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                )}
              </UI.InputWrapper>
            </UI.InputGroup>
            <UI.Div id="CharityPickerList" ref={listRef}>
              {nullable && (
                <>
                  <UI.PickerItem
                    type="button"
                    id="CharityPickerList_0"
                    onClick={() => setCharity(null)}
                    onFocus={() => setFocusIndex(0)}
                    onMouseEnter={() => setFocusIndex(0)}
                    active={activeIndex === 0}
                    focus={focusIndex === 0}
                    tabIndex={-1}
                  >
                    {noneOption || t('none')}
                  </UI.PickerItem>
                  <UI.HR />
                </>
              )}
              {!data && <UI.Loader />}
              {charities.map((charity, index) => (
                <UI.PickerItem
                  type="button"
                  id={`CharityPickerList_${index + 1}`}
                  onClick={() => setCharity(charity.id)}
                  onFocus={() => setFocusIndex(index + 1)}
                  onMouseEnter={() => setFocusIndex(index + 1)}
                  active={activeIndex === index + 1}
                  focus={focusIndex === index + 1}
                  tabIndex={-1}
                  style={{ opacity: loading ? 0.5 : undefined }}
                  key={charity.id}
                >
                  {charity.title}
                </UI.PickerItem>
              ))}
              {data?.project.charities.has_more_pages && (
                <>
                  <UI.HR />
                  <UI.PickerItem
                    type="button"
                    id={`CharityPickerList_${charities.length + 1}`}
                    onClick={() => setLimit((limit) => limit + 20)}
                    onFocus={() => setFocusIndex(charities.length + 1)}
                    onMouseEnter={() => setFocusIndex(charities.length + 1)}
                    active={activeIndex === charities.length + 1}
                    focus={focusIndex === charities.length + 1}
                    tabIndex={-1}
                    disabled={loading}
                  >
                    {t('show_more')}
                  </UI.PickerItem>
                </>
              )}
            </UI.Div>
          </UI.Div>
        )}
      </UI.PickerDropdown>
    </UI.PickerContainer>
  );
};

export default CharityPicker;
